import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check } from 'lucide-react';

/**
 * Sticky numbered-steps rail — Breww "Under the hood" pattern, rounded badges.
 *
 * Uses position:fixed + scroll listener (not CSS sticky) because the shell's
 * <main> has overflow-x:clip which promotes overflow-y to auto and breaks
 * CSS sticky.
 *
 * Visual model:
 *   - Left rail: N rounded badges on a single vertical column, connected by a
 *     progress line that fills as you scroll. Passed steps show a check;
 *     active step scales up + pulses; upcoming steps are outlined.
 *   - Right panel: title/description/pills swap in with a stagger, ghost
 *     numeral watermark behind title.
 *
 * Props: steps, stepScrollVh, header, sideDecoration
 */
const StickyStepsRail = ({
  steps,
  stepScrollVh = 70,
  header = null,
  sideDecoration = null,
}) => {
  const wrapperRef = useRef(null);
  const rafRef = useRef(0);
  const [mode, setMode] = useState('above');
  const [active, setActive] = useState(0);
  const [progress, setProgress] = useState(0); // 0..1 through the wrapper

  const totalVh = steps.length * stepScrollVh + 100;

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;

    const compute = () => {
      const rect = el.getBoundingClientRect();
      const total = el.offsetHeight - window.innerHeight;
      if (rect.top >= 0) {
        setMode('above');
        setActive(0);
        setProgress(0);
      } else if (-rect.top >= total) {
        setMode('below');
        setActive(steps.length - 1);
        setProgress(1);
      } else {
        setMode('pinned');
        const p = -rect.top / total;
        setProgress(p);
        const idx = Math.min(steps.length - 1, Math.floor(p * steps.length));
        setActive((prev) => (prev === idx ? prev : idx));
      }
    };

    const onScroll = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = 0;
        compute();
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    compute();
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [steps.length, stepScrollVh]);

  const jumpTo = (i) => {
    const el = wrapperRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const wrapperTopAbs = window.scrollY + rect.top;
    const total = el.offsetHeight - window.innerHeight;
    const target = wrapperTopAbs + (total * i) / steps.length + 8;
    window.scrollTo({ top: target, behavior: 'smooth' });
  };

  const activeStep = steps[active];

  const pinnedStyle =
    mode === 'pinned'
      ? { position: 'fixed', top: 0, left: 0, right: 0 }
      : mode === 'below'
      ? { position: 'absolute', bottom: 0, left: 0, right: 0 }
      : { position: 'absolute', top: 0, left: 0, right: 0 };

  // Progress line fill height (0..100%) based on active step
  const railFillPct = steps.length > 1 ? (active / (steps.length - 1)) * 100 : 0;

  return (
    <div ref={wrapperRef} className="relative" style={{ height: `${totalVh}vh` }}>
      <div style={{ ...pinnedStyle, height: '100vh' }} className="flex flex-col">
        {header && (
          <div className="pt-16 md:pt-20 pb-4 max-w-6xl mx-auto px-6 w-full">
            {header}
          </div>
        )}

        <div className="flex-1 relative flex items-center">
          {sideDecoration && (
            <div className="pointer-events-none absolute right-[-8%] top-1/2 -translate-y-1/2 hidden lg:block opacity-70">
              {sideDecoration}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-[160px_1fr] gap-10 md:gap-16 w-full max-w-6xl mx-auto px-6">
            {/* ---------- Left rail: rounded badges + progress line ---------- */}
            <div className="relative hidden md:flex flex-col items-center h-[440px]">
              {/* Track (behind badges) */}
              <div
                className="absolute top-0 bottom-0 w-[3px] rounded-full bg-slate-200/70"
                style={{ left: '50%', transform: 'translateX(-50%)' }}
                aria-hidden="true"
              />
              {/* Progress fill (grows as user scrolls) */}
              <motion.div
                className="absolute top-0 w-[3px] rounded-full"
                style={{
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: 'linear-gradient(180deg, #3b82f6 0%, #10b981 100%)',
                  boxShadow: '0 0 12px rgba(59,130,246,0.35)',
                }}
                animate={{ height: `${railFillPct}%` }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                aria-hidden="true"
              />

              {/* Badges */}
              <div className="relative flex flex-col justify-between h-full py-2 w-full items-center">
                {steps.map((s, i) => {
                  const isActive = i === active;
                  const isDone = i < active;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => jumpTo(i)}
                      className="group relative flex items-center justify-center"
                      aria-label={`Go to step ${i + 1}: ${s.title}`}
                    >
                      {/* Outer pulse ring for active step */}
                      {isActive && (
                        <motion.span
                          className="absolute inset-0 rounded-full"
                          style={{ background: 'rgba(59,130,246,0.28)' }}
                          animate={{ scale: [1, 1.8, 1], opacity: [0.6, 0, 0.6] }}
                          transition={{ duration: 2, repeat: Infinity, ease: 'easeOut' }}
                        />
                      )}
                      <motion.div
                        className={`relative flex items-center justify-center rounded-full font-bold transition-colors duration-300 ${
                          isActive
                            ? 'text-white shadow-lg'
                            : isDone
                            ? 'text-white'
                            : 'text-slate-400 group-hover:text-slate-600 border-2 border-slate-200 bg-white'
                        }`}
                        style={
                          isActive
                            ? {
                                background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
                                boxShadow: '0 8px 24px -6px rgba(59,130,246,0.55)',
                              }
                            : isDone
                            ? { background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }
                            : {}
                        }
                        animate={{
                          width: isActive ? 56 : 40,
                          height: isActive ? 56 : 40,
                          fontSize: isActive ? '1.25rem' : '0.95rem',
                        }}
                        transition={{ type: 'spring', stiffness: 260, damping: 22 }}
                      >
                        {isDone ? (
                          <Check className="w-5 h-5" strokeWidth={3} />
                        ) : (
                          <span>{i + 1}</span>
                        )}
                      </motion.div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* ---------- Right panel ---------- */}
            <div className="relative">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeStep.id}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  variants={{
                    hidden: {},
                    visible: { transition: { staggerChildren: 0.06, delayChildren: 0.05 } },
                    exit: {},
                  }}
                  className="relative"
                >
                  {/* Ghost numeral watermark */}
                  <motion.span
                    className="absolute left-0 top-0 select-none font-extrabold leading-[0.8] pointer-events-none"
                    style={{
                      fontSize: 'clamp(9rem, 16vw, 14rem)',
                      transform: 'translate(-6%, -30%)',
                      background: 'linear-gradient(180deg, #eff6ff 0%, #ecfeff 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      zIndex: 0,
                    }}
                    initial={{ opacity: 0, scale: 0.9, x: -30 }}
                    animate={{ opacity: 1, scale: 1, x: 0 }}
                    exit={{ opacity: 0, scale: 0.9, x: 30 }}
                    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    aria-hidden="true"
                  >
                    {active + 1}
                  </motion.span>

                  <div className="relative z-10">
                    <motion.div
                      variants={{
                        hidden: { opacity: 0, y: 20 },
                        visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
                        exit: { opacity: 0, y: -10, transition: { duration: 0.25 } },
                      }}
                      className="text-xs font-bold uppercase tracking-[0.24em] text-blue-600 mb-2"
                    >
                      Step {String(active + 1).padStart(2, '0')} / {String(steps.length).padStart(2, '0')}
                    </motion.div>

                    <motion.h3
                      variants={{
                        hidden: { opacity: 0, y: 24 },
                        visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
                        exit: { opacity: 0, y: -14, transition: { duration: 0.25 } },
                      }}
                      className="text-3xl md:text-5xl font-bold text-slate-900 mb-4"
                    >
                      {activeStep.title}
                    </motion.h3>

                    <motion.p
                      variants={{
                        hidden: { opacity: 0, y: 16 },
                        visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
                        exit: { opacity: 0, transition: { duration: 0.2 } },
                      }}
                      className="text-lg text-slate-500 max-w-xl mb-6"
                    >
                      {activeStep.description}
                    </motion.p>

                    {activeStep.pills && activeStep.pills.length > 0 && (
                      <motion.div
                        variants={{
                          hidden: {},
                          visible: { transition: { staggerChildren: 0.05 } },
                          exit: {},
                        }}
                        className="flex flex-wrap gap-2 mb-6"
                      >
                        {activeStep.pills.map((p) => (
                          <motion.span
                            key={p}
                            variants={{
                              hidden: { opacity: 0, y: 10, scale: 0.9 },
                              visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.4 } },
                              exit: { opacity: 0, transition: { duration: 0.15 } },
                            }}
                            className="inline-flex items-center rounded-full border border-slate-200 bg-white/90 px-4 py-1.5 text-xs font-medium text-slate-600 backdrop-blur-sm"
                          >
                            {p}
                          </motion.span>
                        ))}
                      </motion.div>
                    )}

                    {activeStep.status && (
                      <motion.div
                        variants={{
                          hidden: { opacity: 0, y: 8 },
                          visible: { opacity: 1, y: 0, transition: { duration: 0.45 } },
                          exit: { opacity: 0, transition: { duration: 0.2 } },
                        }}
                        className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-500"
                      >
                        <span className="relative flex h-2 w-2">
                          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                          <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                        </span>
                        {activeStep.status}
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Progress dots */}
              <div className="mt-10 flex items-center gap-2">
                {steps.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => jumpTo(i)}
                    aria-label={`Go to step ${i + 1}`}
                    className={`h-1.5 rounded-full transition-all duration-500 ${
                      i === active
                        ? 'w-10 bg-gradient-to-r from-blue-500 to-emerald-500'
                        : i < active
                        ? 'w-1.5 bg-emerald-400'
                        : 'w-1.5 bg-slate-300 hover:bg-slate-400'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StickyStepsRail;
